// client/src/components/messages/messageUi.js
// Per-sender colours, read-receipt status and bubble grouping for the
// message thread view.

// 12 evenly spread hues. MessageThreadModal.css builds every bubble / name /
// avatar colour as hsl(var(--user-hue), S, L) with fixed S/L, which keeps
// text at WCAG AA contrast for all 12 (bubble text ≥7.4:1, names ≥5.3:1).
// Random hex colours couldn't guarantee that.
const HUES = [0, 30, 50, 90, 140, 170, 195, 215, 250, 275, 300, 330];

export function hueForUser(userId) {
  const str = String(userId ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return HUES[Math.abs(hash) % HUES.length];
}

const idOf = (u) => Number(u?.id || u?.userid || u?.userId || 0);

/**
 * Receipt for one of my own messages.
 *   sending → clock, failed → retry, sent → ✓, read → ✓✓
 * 1:1 is 'read' once the other person has read it; groups once everyone has
 * (readCount/total feed the "Read by 2 of 4" label).
 */
export function getReceipt(message, { participants = [], readState = [], myUserId }) {
  if (message?.failed) return { status: 'failed' };
  if (message?.isOptimistic || !Number(message?.id)) return { status: 'sending' };

  const others = participants.map(idOf).filter((id) => id > 0 && id !== Number(myUserId));
  if (others.length === 0) return { status: 'sent' };

  const watermark = new Map(
    readState.map((r) => [Number(r.userId), Number(r.lastReadMessageId) || 0])
  );
  const readCount = others.filter((id) => (watermark.get(id) || 0) >= Number(message.id)).length;

  return {
    status: readCount === others.length ? 'read' : 'sent',
    readCount,
    total: others.length,
  };
}

const RUN_WINDOW_MS = 5 * 60 * 1000;
const timeOf = (m) => m?.createdAt || m?.created_at || m?.timestamp;
const senderOf = (m) => Number(m?.senderId || m?.sender_id || 0);

function sameRun(a, b) {
  const da = new Date(timeOf(a));
  const db = new Date(timeOf(b));
  return senderOf(a) === senderOf(b)
    && !a.failed && !b.failed
    && da.toDateString() === db.toDateString()
    && db - da < RUN_WINDOW_MS;
}

/**
 * Flat message list → render items: day separators, plus each message tagged
 * with its place in a run from the same sender ('single' | 'first' | 'middle'
 * | 'last'). Drives bubble corner shapes and where the name/avatar appear.
 */
export function buildRenderItems(messages) {
  const items = [];
  let lastDay = null;

  messages.forEach((msg, i) => {
    const date = new Date(timeOf(msg));
    const dayKey = date.toDateString();
    if (dayKey !== lastDay) {
      items.push({ type: 'day', key: `day-${dayKey}`, date });
      lastDay = dayKey;
    }

    const joinsPrev = i > 0 && sameRun(messages[i - 1], msg);
    const joinsNext = i < messages.length - 1 && sameRun(msg, messages[i + 1]);
    const position = joinsPrev && joinsNext ? 'middle'
      : joinsPrev ? 'last'
      : joinsNext ? 'first'
      : 'single';

    items.push({ type: 'message', key: msg.id ?? `idx-${i}`, message: msg, position });
  });

  return items;
}

export function formatDayLabel(date, now = new Date()) {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === now.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}

export function formatClock(timestamp) {
  return new Date(timestamp).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function initials(name = '') {
  return String(name).split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]).join('').toUpperCase() || '?';
}
